import { ReactNode, createContext, useEffect } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { loadMarketUiConfig } from '~/app/market-config/load-market-config';
import { AemMarketConfig } from '~/app/market-config/models/aem-market-config';
import { CarConfiOption, CarConfiUiConfiguration } from '~/app/market-config/models/car-confi-ui-configuration';
import { useMarketConfig } from '~/app/market-config/use-market-config';
import { ConfiguratorOption, PossibleConfiguratorOptionValues } from '~/app/models/configurator-options';

export const MarketUiContext = createContext({} as CarConfiUiConfiguration);

export function MarketUiProvider({ aemConfig, children }: { aemConfig: AemMarketConfig; children: ReactNode }) {
	const marketConfig = useMarketConfig();
	const { i18n } = useI18n();
	const config = loadMarketUiConfig(aemConfig);
	const showAccessoriesAndEquipments = config.showAccessoriesExclusive || config.showAccessoriesInclusive;
	const combinations = marketConfig.config.combinations;

	// Add accessories and equipments to the combinations
	useEffect(() => {
		showAccessoriesAndEquipments && combinations.push('accessoriesAndEquipments');
	}, [showAccessoriesAndEquipments, combinations]);

	// FIXME: there should be some kind of reducer instead of setting those values right away on the marketConfig directly
	// Question: Why do we have in the ui config the options for custom components and then write them to the market config
	// Wouldn't it be easier to keep the config in one place?

	marketConfig.config.configuratorOptions = setConfiguratorOptions(combinations, config.options).map((option) => ({
		...option,
		label: i18n(option.label),
	}));
	marketConfig.config.defaultOption = marketConfig.config.configuratorOptions[0]?.id || '';

	return <MarketUiContext.Provider value={{ ...config }}>{children}</MarketUiContext.Provider>;
}

const setConfiguratorOptions = (combinations: PossibleConfiguratorOptionValues[], options: CarConfiOption) => {
	return combinations
		.map((item) => {
			const optionItem = initOptionItem(item, options);
			if (!optionItem) return;
			return optionItem;
		})
		.filter(Boolean) as ConfiguratorOption[];
};

// FIXME: this can be refactored or even completely kicked out
// NOTE: This code checks for square brakets to define in which order some items should be shown
// This feature is only used for the interior items and as a only once used feature I would suggest to remove that code and
// add static properties to the component
const initOptionItem = (item: string, options: CarConfiOption): ConfiguratorOption | undefined => {
	let optionItem = item as keyof typeof options;
	let optionProps: string[] | undefined;
	/* eslint-disable-next-line prefer-regex-literals */
	const regex = new RegExp('^(.*)\\[(.*)\\]$', 'gm');

	if (regex.test(item)) {
		// eslint-disable-next-line no-useless-escape
		optionItem = item.match(/^[^\[]+/)?.[0] as keyof typeof options;
		optionProps = item
			.match(/\[(.*?)\]/)?.[1]
			.split(',')
			.map((value) => value.trim());
	}
	if (optionItem && options[optionItem]) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return options[optionItem]({ combinations: optionProps as any });
	}

	console.warn(`option ${optionItem} is missing in CarConfiOption.`);
};
